<template>
  <section class="asset-section">
    <div class="section-head section-head--split">
      <div>
        <span class="section-label">{{ sectionCopy.label }}</span>
        <h2>{{ sectionCopy.title }}</h2>
        <p>{{ sectionCopy.description }}</p>
      </div>
      <div class="toolbar-inline">
        <el-input
          v-model="imageFilters.query"
          clearable
          :placeholder="sectionCopy.searchPlaceholder"
          @keyup.enter="loadMaterialEntries"
          @clear="loadMaterialEntries"
        />
        <el-select v-model="imageFilters.scope" @change="loadMaterialEntries">
          <el-option v-if="linkedProjectId" label="当前项目" value="current_project" />
          <el-option label="我可见的全部项目" value="all_projects" />
        </el-select>
        <el-button type="primary" :loading="loadingMaterials" @click="loadMaterialEntries">刷新{{ sectionCopy.refreshLabel }}</el-button>
      </div>
    </div>

    <div v-if="linkedProjectId && imageFilters.scope === 'current_project'" class="inline-note">
      当前展示的是项目 {{ linkedProjectId }} 已上传或已生成的{{ sectionCopy.refreshLabel }}素材。
    </div>

    <div v-if="activeTab === 'audio'" class="audio-category-strip">
      <button
        v-for="option in audioCategoryOptions"
        :key="option.value"
        type="button"
        class="audio-category-chip"
        :class="{ 'audio-category-chip--active': activeAudioCategory === option.value }"
        @click="activeAudioCategory = option.value"
      >
        {{ option.label }}
      </button>
    </div>

    <section v-if="filteredMaterialEntries.length" class="material-groups">
      <section v-for="group in groupedMaterialEntries" :key="group.key" class="project-group">
        <header class="project-group__head" :class="{ 'project-group__head--current': group.isCurrentProject }">
          <div>
            <h3>{{ group.heading }}</h3>
            <p>{{ group.summary }}</p>
          </div>
        </header>

        <div class="image-grid">
          <MaterialLibraryEntryCard
            v-for="item in group.entries"
            :key="item.id"
            :active-tab="activeTab"
            :entry="item"
            :preview-failed="Boolean(failedMedia[item.id])"
            :linked-project-id="linkedProjectId"
            :linked-scene-number="linkedSceneNumber"
            :linked-asset-type-label="linkedAssetTypeLabel"
            :linked-audio-role="linkedAudioRole"
            :is-consistency-asset-image-mode="isConsistencyAssetImageMode"
            :is-final-audio-mode="isFinalAudioMode"
            :deleting-material-id="deletingMaterialId"
            :format-date="formatDate"
            :get-material-source-label="getMaterialSourceLabel"
            :can-delete-material="canDeleteMaterial"
            :handle-delete-material="handleDeleteMaterial"
            :handle-use-material-as-final-audio="handleUseMaterialAsFinalAudio"
            :handle-use-material-as-consistency-asset-image="handleUseMaterialAsConsistencyAssetImage"
            :handle-use-material-as-scene-image="handleUseMaterialAsSceneImage"
            :link-material-to-workflow="linkMaterialToWorkflow"
            @media-error="markMediaFailed"
          />
        </div>
      </section>
    </section>

    <el-empty v-else :description="emptyDescription" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import MaterialLibraryEntryCard from './MaterialLibraryEntryCard.vue'
import {
  AUDIO_MATERIAL_CATEGORY_OPTIONS,
  filterAudioMaterialEntries,
  getMaterialSectionCopy,
  type AudioMaterialCategory,
} from './audioMaterialLibrary'
import { groupMaterialEntriesByProject } from './materialLibraryGrouping'
import type { MaterialLibraryEntry } from './types'

const props = defineProps<{
  activeTab: 'image' | 'video' | 'audio'
  linkedProjectId: number | null
  linkedSceneNumber: number | null
  linkedAssetTypeLabel: string
  linkedAudioRole: 'narration' | 'bgm' | null
  isConsistencyAssetImageMode: boolean
  isFinalAudioMode: boolean
  imageFilters: {
    query: string
    scope: 'current_project' | 'all_projects'
  }
  materialEntries: MaterialLibraryEntry[]
  loadingMaterials: boolean
  deletingMaterialId: number | null
  formatDate: (value?: string | null) => string
  getMaterialSourceLabel: (source: string) => string
  canDeleteMaterial: (entry: MaterialLibraryEntry) => boolean
  loadMaterialEntries: () => void | Promise<void>
  handleDeleteMaterial: (entry: MaterialLibraryEntry) => void | Promise<void>
  handleUseMaterialAsFinalAudio: (entry: MaterialLibraryEntry) => void
  handleUseMaterialAsConsistencyAssetImage: (entry: MaterialLibraryEntry) => void
  handleUseMaterialAsSceneImage: (entry: MaterialLibraryEntry) => void
  linkMaterialToWorkflow: (entry: MaterialLibraryEntry) => void
}>()

const failedMedia = ref<Record<number, boolean>>({})
const activeAudioCategory = ref<AudioMaterialCategory>('all')

const audioCategoryOptions = AUDIO_MATERIAL_CATEGORY_OPTIONS

const filteredMaterialEntries = computed(() => {
  if (props.activeTab !== 'audio') {
    return props.materialEntries
  }
  return filterAudioMaterialEntries(props.materialEntries, activeAudioCategory.value)
})

const groupedMaterialEntries = computed(() => groupMaterialEntriesByProject(filteredMaterialEntries.value, props.linkedProjectId))

const sectionCopy = computed(() => getMaterialSectionCopy(props.activeTab))

const emptyDescription = computed(() => {
  if (props.activeTab === 'audio' && activeAudioCategory.value !== 'all') {
    return activeAudioCategory.value === 'narration'
      ? '当前筛选下还没有旁白素材。你在终版音频工作台里生成的旁白，刷新后会出现在这里。'
      : '当前筛选下还没有 BGM 素材。你在终版音频工作台里上传的背景音乐，刷新后会出现在这里。'
  }
  return sectionCopy.value.emptyDescription
})

watch(
  () => props.materialEntries.map((item) => `${item.id}:${item.image_url}`).join('|'),
  () => {
    failedMedia.value = {}
  }
)

watch(
  () => [props.activeTab, props.linkedAudioRole] as const,
  ([activeTab, linkedAudioRole]) => {
    if (activeTab !== 'audio') {
      activeAudioCategory.value = 'all'
      return
    }

    activeAudioCategory.value = linkedAudioRole || 'all'
  },
  { immediate: true },
)

function markMediaFailed(id: number) {
  failedMedia.value = {
    ...failedMedia.value,
    [id]: true,
  }
}
</script>

<style scoped>
.asset-section {
  margin-top: 24px;
}

.section-head,
.section-head--split,
.image-card__head,
.image-card__footer,
.toolbar-inline,
.image-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.section-label {
  margin: 0;
  color: #c2410c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.section-head p,
.inline-note {
  color: #475569;
  line-height: 1.8;
}

.toolbar-inline {
  align-items: center;
}

.toolbar-inline > * {
  min-width: 0;
}

.audio-category-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.audio-category-chip {
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.88);
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.audio-category-chip--active {
  border-color: rgba(14, 116, 144, 0.32);
  background: rgba(224, 242, 254, 0.92);
  color: #0f4c81;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.material-groups {
  display: grid;
  gap: 20px;
  margin-top: 20px;
}

.project-group,
.inline-note {
  padding: 22px;
  border-radius: 24px;
  background: rgba(248, 250, 252, 0.86);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.project-group__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.project-group__head h3 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
}

.project-group__head p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.project-group__head--current {
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(59, 130, 246, 0.12);
}

.inline-note {
  margin-top: 18px;
  background: rgba(236, 253, 245, 0.8);
  border-color: rgba(16, 185, 129, 0.16);
}

@media (max-width: 1080px) {
  .image-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .section-head--split,
  .toolbar-inline,
  .project-group__head {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>